import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Col, Form, Input, Row, Select } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { projectsApi } from '../projects.api';
import type { BrandLanguage, BrandSettings, Project } from '../types';

interface BrandFormValues {
  primary: string;
  secondary: string;
  accent: string;
  headingFont: string;
  bodyFont: string;
  logoUrl: string;
  toneOfVoice: string;
  brandIdentity: string;
  targetAudience: string;
  language: BrandLanguage;
}

const toValues = (brand: BrandSettings): BrandFormValues => ({
  primary: brand.colors.primary,
  secondary: brand.colors.secondary,
  accent: brand.colors.accent,
  headingFont: brand.typography.headingFont,
  bodyFont: brand.typography.bodyFont,
  logoUrl: brand.logoUrl ?? '',
  toneOfVoice: brand.toneOfVoice,
  brandIdentity: brand.brandIdentity,
  targetAudience: brand.targetAudience,
  language: brand.language ?? 'en',
});

export function BrandForm({
  orgId,
  project,
  canEdit,
}: {
  orgId: string;
  project: Project;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { control, handleSubmit } = useForm<BrandFormValues>({
    defaultValues: toValues(project.brand),
  });

  const mutation = useMutation({
    mutationFn: (values: BrandFormValues) =>
      projectsApi.updateBrand(orgId, project.id, {
        colors: { primary: values.primary, secondary: values.secondary, accent: values.accent },
        typography: { headingFont: values.headingFont, bodyFont: values.bodyFont },
        logoUrl: values.logoUrl.trim() || null,
        toneOfVoice: values.toneOfVoice,
        brandIdentity: values.brandIdentity,
        targetAudience: values.targetAudience,
        language: values.language,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['project', orgId, project.id] });
      message.success('Brand settings saved');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to save brand'),
  });

  const colorField = (name: 'primary' | 'secondary' | 'accent', label: string) => (
    <Col span={8}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Form.Item label={label}>
            <Input
              type="color"
              {...field}
              disabled={!canEdit}
              style={{ width: '100%', height: 40 }}
            />
          </Form.Item>
        )}
      />
    </Col>
  );

  const textField = (
    name: keyof BrandFormValues,
    label: string,
    textarea = false,
    placeholder = '',
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Form.Item label={label}>
          {textarea ? (
            <Input.TextArea {...field} rows={3} disabled={!canEdit} placeholder={placeholder} />
          ) : (
            <Input {...field} disabled={!canEdit} placeholder={placeholder} />
          )}
        </Form.Item>
      )}
    />
  );

  return (
    <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
      <Row gutter={16}>
        {colorField('primary', 'Primary')}
        {colorField('secondary', 'Secondary')}
        {colorField('accent', 'Accent')}
      </Row>
      <Row gutter={16}>
        <Col span={12}>{textField('headingFont', 'Heading font', false, 'Inter')}</Col>
        <Col span={12}>{textField('bodyFont', 'Body font', false, 'Inter')}</Col>
      </Row>
      {textField('logoUrl', 'Logo URL', false, 'https://…')}
      <Controller
        control={control}
        name="language"
        render={({ field }) => (
          <Form.Item
            label="Content language"
            help="Generated posts follow this language (bilingual writes Arabic first, then English)."
          >
            <Select<BrandLanguage>
              {...field}
              disabled={!canEdit}
              style={{ maxWidth: 320 }}
              options={[
                { value: 'en', label: 'English' },
                { value: 'ar', label: 'العربية (Arabic)' },
                { value: 'both', label: 'Arabic + English (bilingual)' },
              ]}
            />
          </Form.Item>
        )}
      />
      {textField('toneOfVoice', 'Tone of voice', true, 'e.g. Confident, friendly, concise')}
      {textField('brandIdentity', 'Brand identity', true, 'What the brand stands for')}
      {textField('targetAudience', 'Target audience', true, 'Who you are speaking to')}
      {canEdit ? (
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Save brand settings
        </Button>
      ) : null}
    </Form>
  );
}
